import { useState } from "react";

import { anfrage, ApiFehler } from "../api/client";
import { Hinweis, Knopf, Modal, Spinner } from "./Basis";

interface ZerezTreffer {
  zerez_id: string;
  modell: string;
  hersteller: string | null;
  leistung_kw: number | null;
  norm: string | null;
  status: string | null;
  gueltig: boolean;
  verifiziert: boolean;
}

interface ZerezErgebnis {
  suchtext: string;
  begriff: string | null;
  treffer: ZerezTreffer[];
  vorschlag: string | null;
}

/**
 * Sucht die ZEREZ-ID zum Wechselrichter im öffentlichen ZEREZ-Register (zerez.net) und lässt den Anmelder
 * den passenden Treffer wählen. Eindeutige Treffer trägt die Plattform nach dem Upload schon selbst ein –
 * dieser Dialog ist für die Fälle mit mehreren Kandidaten oder einem abweichenden Modellnamen.
 */
export function ZerezSucheKnopf({ anlageId, wechselrichter, beiWahl }: { anlageId: number; wechselrichter: string; beiWahl: (zerezId: string) => void }) {
  const [offen, setOffen] = useState(false);
  const [text, setText] = useState("");
  const [ergebnis, setErgebnis] = useState<ZerezErgebnis | null>(null);
  const [laedt, setLaedt] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function suchen(suchtext: string) {
    setFehler(null);
    setLaedt(true);
    try {
      setErgebnis(await anfrage<ZerezErgebnis>(`/api/anlagen/${anlageId}/zerez-suche?q=${encodeURIComponent(suchtext)}`));
    } catch (e) {
      setErgebnis(null);
      setFehler(e instanceof ApiFehler ? e.message : "Suche fehlgeschlagen");
    } finally {
      setLaedt(false);
    }
  }

  function oeffnen() {
    setText(wechselrichter);
    setErgebnis(null);
    setFehler(null);
    setOffen(true);
    if (wechselrichter.trim()) void suchen(wechselrichter);
  }

  return (
    <>
      <button type="button" className="mt-1 text-xs font-semibold text-neutral-600 underline underline-offset-2" onClick={oeffnen}>
        Im ZEREZ-Register suchen
      </button>
      {offen && (
        <Modal offen breit titel="ZEREZ-ID suchen" beiSchliessen={() => setOffen(false)} fuss={<Knopf onClick={() => setOffen(false)}>Schließen</Knopf>}>
          <div className="space-y-3 text-sm">
            {/* Kein <form>: der Dialog kann innerhalb des Prüf-Formulars liegen (verschachtelte Formulare sind ungültig). */}
            <div className="flex gap-2">
              <input
                data-autofokus
                className="min-w-0 flex-1 rounded-lg border border-neutral-300 px-3 py-2"
                placeholder="Wechselrichter, z. B. Symo GEN24 10.0 Plus"
                value={text}
                onChange={(e) => setText(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    if (text.trim()) void suchen(text.trim());
                  }
                }}
              />
              <Knopf variante="primaer" laedt={laedt} disabled={!text.trim()} onClick={() => void suchen(text.trim())}>Suchen</Knopf>
            </div>
            {laedt && <Spinner label="Suche im ZEREZ-Register…" />}
            {fehler && <Hinweis art="fehler">{fehler}</Hinweis>}
            {ergebnis && !laedt && ergebnis.treffer.length === 0 && (
              <Hinweis art="info">Kein Treffer. Tipp: nur den Modellnamen eingeben, ohne Hersteller (z. B. „SUN2000-10KTL-M1“).</Hinweis>
            )}
            {ergebnis && !laedt && ergebnis.treffer.length > 0 && (
              <>
                <p className="text-xs text-neutral-500">
                  {ergebnis.treffer.length} Treffer für „{ergebnis.begriff}“ – Quelle: zerez.net
                </p>
                <ul className="divide-y divide-neutral-100 rounded-xl border border-neutral-200">
                  {ergebnis.treffer.map((t) => (
                    <li key={`${t.zerez_id}-${t.modell}`} className="flex flex-wrap items-center gap-3 px-3 py-2.5">
                      <div className="min-w-0 flex-1">
                        <p className="font-semibold">
                          {t.modell}
                          {ergebnis.vorschlag === t.zerez_id && <span className="ml-2 rounded-full bg-ampel-gruenHell px-2 py-0.5 text-xs text-ampel-gruen">passt genau</span>}
                        </p>
                        <p className="text-xs text-neutral-500">
                          {[t.hersteller, t.leistung_kw !== null ? `${t.leistung_kw} kW` : null, t.norm].filter(Boolean).join(" · ")}
                        </p>
                        <p className="text-xs">
                          <span className="font-mono">{t.zerez_id}</span>
                          <span className={`ml-2 ${t.gueltig ? "text-ampel-gruen" : "text-ampel-rot"}`}>{t.gueltig ? "Zertifikat gültig" : (t.status ?? "Status unbekannt")}</span>
                        </p>
                      </div>
                      <Knopf
                        variante={ergebnis.vorschlag === t.zerez_id ? "primaer" : "sekundaer"}
                        onClick={() => {
                          beiWahl(t.zerez_id);
                          setOffen(false);
                        }}
                      >
                        Übernehmen
                      </Knopf>
                    </li>
                  ))}
                </ul>
              </>
            )}
          </div>
        </Modal>
      )}
    </>
  );
}
